import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map, mergeMap, of, throwError, timer } from 'rxjs';
import { keysToCamelCase, toSnakeCase } from 'src/app/utils/case-converter';
import { FLEET_MOCK_LATENCY, FLEET_MOCK_ROLE, fleetRolePages, applyFleetMockQueryParam } from './fleet-mock.config';
import { FleetMockApi, MockApiError, MockResponse } from './fleet-mock.api';
import { FleetMockStore } from './fleet-mock.store';

/**
 * Responde en el navegador las rutas del contrato de flotas mientras no existe
 * el backend (docs/flotas-drivers/README.md#mocks). Solo se registra con
 * `environment.fleetMock` (local). El resto de peticiones pasan tal cual.
 *
 * Además, si existe la flota del titular (un escenario elegido o una flota
 * creada en el mock), añade su rol de flota a la respuesta real de `/me`.
 */
@Injectable()
export class FleetMockInterceptor implements HttpInterceptor {
  private readonly store: FleetMockStore;
  private readonly api: FleetMockApi;

  constructor() {
    const reseed = applyFleetMockQueryParam();
    this.store = new FleetMockStore(reseed);
    this.api = new FleetMockApi(this.store);
    console.info(
      '[FleetMock] Activo. Escenario:',
      this.store.settings.scenario ?? '(ninguno)',
      '· cambiar con ?fleetMock=alta|panel-vacio|con-altas|rechazo|aprobada,taxi|vtc,owner|manager|accountant · off · reset',
    );
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.method === 'GET' && /\/backoffice\/me(\?|$)/.test(req.url)) {
      return next.handle(req).pipe(map((event) => this.withFleetRole(event)));
    }
    if (!this.api.owns(req.url)) {
      return next.handle(req);
    }

    const latency = FLEET_MOCK_LATENCY[0] + Math.random() * (FLEET_MOCK_LATENCY[1] - FLEET_MOCK_LATENCY[0]);
    const mockReq = req.clone({
      body: req.body instanceof FormData || req.body === null ? req.body : keysToCamelCase(req.body),
    });

    let response: MockResponse | null;
    try {
      response = this.api.handle(mockReq);
      if (!response) {
        throw new MockApiError(405, { message: `Método ${req.method} no disponible en el mock.`, error_type: 'resource_not_found' });
      }
    } catch (error) {
      const apiError =
        error instanceof MockApiError
          ? error
          : new MockApiError(500, { message: `Error del mock: ${(error as Error).message}`, error_type: 'internal_error' });
      if (!(error instanceof MockApiError)) {
        console.error('[FleetMock]', error);
      }
      return timer(latency).pipe(
        mergeMap(() =>
          throwError(
            () =>
              new HttpErrorResponse({
                status: apiError.status,
                statusText: 'Mock',
                url: req.urlWithParams,
                error: toSnakeCase(apiError.body),
              }),
          ),
        ),
      );
    }

    const done = response;
    return timer(latency).pipe(
      mergeMap(() =>
        of(
          new HttpResponse({
            status: done.status,
            url: req.urlWithParams,
            headers: new HttpHeaders(done.headers ?? {}),
            body: done.blob ?? (done.body === null ? null : toSnakeCase(done.body)),
          }),
        ),
      ),
    );
  }

  /** Añade el rol de flota del titular (y sus pantallas) a la respuesta real de `/me`. */
  private withFleetRole(event: HttpEvent<unknown>): HttpEvent<unknown> {
    const fleet = this.store.scenarioFleet();
    if (!(event instanceof HttpResponse) || !fleet || !event.body) {
      return event;
    }
    const raw = event.body as Record<string, any>;
    const me = (raw['data'] && typeof raw['data'] === 'object' ? raw['data'] : raw) as Record<string, any>;
    const role = FLEET_MOCK_ROLE[this.store.settings.role];
    const fleetView = this.store.toFleet(fleet, false);
    const mockRole = {
      id: `mock-fleet-role-${fleet.id}`,
      user_id: me['id'] ?? null,
      user_email: me['email'] ?? null,
      role_id: `mock-${role}`,
      role_name: role,
      permission_name: role,
      scope_type: 'fleet',
      scope_id: fleet.id,
      extra_id: fleet.id,
      type_id: 'mock-fleet',
      scope_name: fleet.name,
      scope: { type: 'fleet', id: fleet.id, name: fleet.name, kind: fleet.kind, mode: fleet.mode, status: fleetView.status },
      created_at: fleet.createdAt,
      updated_at: fleet.createdAt,
    };
    const pages = fleetRolePages(role, fleet.kind).map((name) => ({ id: `mock-page-${name}`, name }));
    const knownPages = new Set((me['pages'] ?? []).map((p: { name: string }) => p.name));
    const patched = {
      ...me,
      roles: [...(me['roles'] ?? []).filter((r: { scope_type?: string }) => r.scope_type !== 'fleet'), mockRole],
      pages: [...(me['pages'] ?? []), ...pages.filter((p) => !knownPages.has(p.name))],
    };
    return event.clone({ body: raw['data'] && typeof raw['data'] === 'object' ? { ...raw, data: patched } : patched });
  }
}
